/* eslint-disable */
import React, {useState, useMemo, useEffect, useCallback} from 'react';
import T from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {
  DialogBody,
  DialogField,
  DialogFields,
  DashboardTimeInner,
  DashboardInnerDescription,
  DashboardTimeDelete,
  DialogLinkButton,
  DialogSubTitle
} from 'routes/App/dashboard/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import DialogTitle from 'components/DialogTitle/styles';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import FieldLabel from 'components/FieldLabel/styles';
import SwitchS2E from 'components/Switch';
import SingleDatepickerDrop from 'components/SingleDatepickerDrop';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {intervals} from 'routes/App/settings/helpers';

const TIME_OPTIONS = intervals('12:00 AM', '11:30 PM', 30);
const CLOSING_TIME_OPTIONS = [...TIME_OPTIONS, '11:59 PM'];

const timeStringToHourMinute = (timeStr) => {
  const m = moment(timeStr, 'h:mm A');
  return {hour: m.hour(), minute: m.minute()};
};

const buildSpecialHoursBody = (startsOn, endsOn, closedAllDay, timeRanges) => {
  const type = closedAllDay ? 'closing_hours' : 'opening_hours';
  const body = {
    startsOn: moment(startsOn).format('YYYY-MM-DD'),
    endsOn: moment(endsOn).format('YYYY-MM-DD'),
    type,
    times: []
  };
  if (type === 'opening_hours' && timeRanges?.length) {
    body.times = timeRanges
      .filter((r) => r.openTime && r.closingTime)
      .map((r) => ({
        start: timeStringToHourMinute(r.openTime),
        end: timeStringToHourMinute(r.closingTime)
      }));
  } else {
    body.times = null;
  }
  return body;
};

const timeRangesOverlap = (ranges) => {
  const filled = ranges.filter((r) => r.openTime && r.closingTime);
  for (let i = 0; i < filled.length; i++) {
    const a = moment(filled[i].openTime, 'h:mm A');
    const b = moment(filled[i].closingTime, 'h:mm A');
    for (let j = i + 1; j < filled.length; j++) {
      const c = moment(filled[j].openTime, 'h:mm A');
      const d = moment(filled[j].closingTime, 'h:mm A');
      if (a.isSameOrBefore(d) && b.isSameOrAfter(c)) return true;
    }
  }
  return false;
};

const doRangesOverlap = (existingList, startsOn, endsOn) => {
  if (!Array.isArray(existingList) || existingList.length === 0) return false;
  const start = moment(startsOn).startOf('day');
  const end = moment(endsOn).startOf('day');
  for (const rule of existingList) {
    const ruleStartsOn = rule.startsOn ?? rule.StartsOn;
    const ruleEndsOn = rule.endsOn ?? rule.EndsOn;
    if (!ruleStartsOn || !ruleEndsOn) continue;
    const ruleStart = moment(ruleStartsOn).startOf('day');
    const ruleEnd = moment(ruleEndsOn).startOf('day');
    if (start.isSameOrBefore(ruleEnd) && end.isSameOrAfter(ruleStart)) {
      return true;
    }
  }
  return false;
};

const getMinTimeMoment = (nowInTz) => {
  const minMin = Math.ceil(nowInTz.minute() / 30) * 30;
  if (minMin === 60) {
    return nowInTz
      .clone()
      .startOf('hour')
      .add(1, 'hour')
      .second(0)
      .millisecond(0);
  }
  return nowInTz.clone().minute(minMin).second(0).millisecond(0);
};

const AddSpecialHoursModal = ({
  open,
  onClose,
  onSave,
  existingSpecialHours = [],
  timezone,
  loading
}) => {
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [closedAllDay, setClosedAllDay] = useState(true);
  const [timeRanges, setTimeRanges] = useState([
    {id: `tr-${Date.now()}`, openTime: '', closingTime: ''}
  ]);
  const [warning, setWarning] = useState('');
  const [overlapError, setOverlapError] = useState('');

  const addTimeRange = useCallback(() => {
    setTimeRanges((prev) => [
      ...prev,
      {
        id: `tr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        openTime: '',
        closingTime: ''
      }
    ]);
  }, []);

  const updateTimeRange = useCallback((id, field, value) => {
    setTimeRanges((prev) =>
      prev.map((r) => (r.id === id ? {...r, [field]: value} : r))
    );
  }, []);

  const removeTimeRange = useCallback((id) => {
    setTimeRanges((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
    );
  }, []);

  useEffect(() => {
    if (open) {
      const now = new Date();
      setStartDate(now);
      setEndDate(now);
      setClosedAllDay(true);
      setTimeRanges([{id: `tr-${Date.now()}`, openTime: '', closingTime: ''}]);
      setWarning('');
      setOverlapError('');
    }
  }, [open]);

  const resetForm = () => {
    setStartDate(today);
    setEndDate(today);
    setClosedAllDay(true);
    setTimeRanges([{id: `tr-${Date.now()}`, openTime: '', closingTime: ''}]);
    setWarning('');
    setOverlapError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const minEndDate = startDate
    ? moment(startDate).isAfter(moment())
      ? startDate
      : today
    : today;

  const nowInTz = useMemo(
    () =>
      timezone != null && timezone !== ''
        ? moment().utcOffset(Number(timezone))
        : moment(),
    [timezone]
  );

  const isCurrentDayInRange = useMemo(() => {
    if (!startDate || !endDate) return false;
    const storeTodayStr = nowInTz.format('YYYY-MM-DD');
    const toDateStr = (d) =>
      timezone != null && timezone !== ''
        ? moment(d).utcOffset(Number(timezone)).format('YYYY-MM-DD')
        : moment(d).format('YYYY-MM-DD');
    const startStr = toDateStr(startDate);
    const endStr = toDateStr(endDate);
    return storeTodayStr >= startStr && storeTodayStr <= endStr;
  }, [startDate, endDate, timezone, nowInTz]);

  const minTimeMoment = useMemo(() => getMinTimeMoment(nowInTz), [nowInTz]);

  const isTimeOptionDisabled = useCallback(
    (option) => {
      if (!isCurrentDayInRange) return false;
      const m = moment(option, 'h:mm A');
      const optionMoment = nowInTz
        .clone()
        .startOf('day')
        .hour(m.hour())
        .minute(m.minute())
        .second(0)
        .millisecond(0);
      return optionMoment.isBefore(minTimeMoment);
    },
    [isCurrentDayInRange, nowInTz, minTimeMoment]
  );

  const timeRangeErrors = useMemo(() => {
    if (closedAllDay) return {invalidOrder: false, overlaps: false};
    const filled = timeRanges.filter((r) => r.openTime && r.closingTime);
    let invalidOrder = false;
    for (const r of filled) {
      const start = moment(r.openTime, 'h:mm A');
      const end = moment(r.closingTime, 'h:mm A');
      if (end.isSameOrBefore(start)) {
        invalidOrder = true;
        break;
      }
    }
    const overlaps = timeRangesOverlap(timeRanges);
    return {invalidOrder, overlaps};
  }, [closedAllDay, timeRanges]);

  const hasAtLeastOneCompleteRange = useMemo(
    () => !closedAllDay && timeRanges.some((r) => r.openTime && r.closingTime),
    [closedAllDay, timeRanges]
  );

  const hasDateRangeOverlap = useMemo(
    () => doRangesOverlap(existingSpecialHours, startDate, endDate),
    [existingSpecialHours, startDate, endDate]
  );

  const canSave = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (moment(endDate).isBefore(moment(startDate).startOf('day')))
      return false;
    if (hasDateRangeOverlap) return false;
    if (!closedAllDay) {
      if (!hasAtLeastOneCompleteRange) return false;
      if (timeRangeErrors.invalidOrder) return false;
      if (timeRangeErrors.overlaps) return false;
    }
    return true;
  }, [
    startDate,
    endDate,
    closedAllDay,
    hasAtLeastOneCompleteRange,
    timeRangeErrors,
    hasDateRangeOverlap
  ]);

  const dateRangeOverlapMessage = hasDateRangeOverlap
    ? closedAllDay
      ? 'You cannot close the store for the whole day for dates that already have special hours or closure rules.'
      : 'A date range already exists for these dates. Adjust the dates to avoid overlap.'
    : null;

  const displayWarning =
    dateRangeOverlapMessage ||
    (timeRangeErrors.invalidOrder
      ? 'Closing time must be later than opening time. Overnight hours must be split across two dates.'
      : timeRangeErrors.overlaps
      ? 'A time range already exists for these hours. Adjust the times to avoid overlap.'
      : overlapError || warning);

  const handleSave = () => {
    setOverlapError('');
    if (!canSave) return;
    const body = buildSpecialHoursBody(
      startDate,
      endDate,
      closedAllDay,
      timeRanges
    );
    onSave(body);
  };

  return (
    <Dialog open={open} scroll='body' onClose={handleClose}>
      <DialogContainer className='DialogMd'>
        <DialogClose onClick={handleClose} />
        <DialogTitle className='DialogTitleMarginBottom'>
          Add special hours
        </DialogTitle>
        <DialogSubTitle className='mBottomLarge'>
          Set dates you're closed or have special hours.
        </DialogSubTitle>
        <DialogBody className='positionRelative'>
          <DialogFields>
            <DialogField className='w50'>
              <FieldLabel className='FieldLabelLg'>Start Date</FieldLabel>
              <SingleDatepickerDrop
                value={startDate}
                minDate={today}
                disablePast
                onChange={(date) => {
                  setStartDate(date);
                  if (
                    endDate &&
                    moment(endDate).isBefore(moment(date).startOf('day'))
                  ) {
                    setEndDate(date);
                  }
                }}
              />
            </DialogField>
            <DialogField className='w50'>
              <FieldLabel className='FieldLabelLg'>End Date</FieldLabel>
              <SingleDatepickerDrop
                value={endDate}
                minDate={minEndDate}
                disablePast={false}
                onChange={setEndDate}
              />
            </DialogField>
          </DialogFields>

          <DialogFields>
            <DialogField className='w100 dFlex AlignStart'>
              <SwitchS2E
                checked={closedAllDay}
                onChange={(e) => {
                  const isClosedAllDay = e.target.checked;
                  setClosedAllDay(isClosedAllDay);
                  if (!isClosedAllDay) {
                    setTimeRanges([
                      {id: `tr-${Date.now()}`, openTime: '', closingTime: ''}
                    ]);
                  }
                }}
                name='specialHoursToggle'
                label={
                  closedAllDay ? 'Closed all day' : 'Open with special hours'
                }
                labelPlacement='start'
              />
            </DialogField>
          </DialogFields>

          {!closedAllDay && (
            <DialogFields className='column relative'>
              {timeRanges.map((range) => (
                <DialogFields key={range.id} className='w100 dFlex AlignStart'>
                  <DialogField className='w40'>
                    <FieldLabel className='FieldLabelLg'>Open time</FieldLabel>
                    <DashboardTimeInner className='Say2EatSelect Say2EatSelectLight Say2EatSelectTime Say2EatSelectTimeWidth100 paddingL0 minWidth160'>
                      <FormControl fullWidth>
                        <Select
                          displayEmpty
                          value={range.openTime}
                          onChange={(e) =>
                            updateTimeRange(
                              range.id,
                              'openTime',
                              e.target.value
                            )
                          }
                          renderValue={(v) => v || 'Select time'}
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            }
                          }}
                        >
                          {TIME_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt}
                              value={opt}
                              disabled={isTimeOptionDisabled(opt)}
                            >
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </DashboardTimeInner>
                  </DialogField>
                  <DialogField className='w40'>
                    <FieldLabel className='FieldLabelLg'>
                      Closing time
                    </FieldLabel>
                    <DashboardTimeInner className='Say2EatSelect Say2EatSelectLight Say2EatSelectTime Say2EatSelectTimeWidth100 paddingL0 minWidth160'>
                      <FormControl fullWidth>
                        <Select
                          displayEmpty
                          value={range.closingTime}
                          onChange={(e) =>
                            updateTimeRange(
                              range.id,
                              'closingTime',
                              e.target.value
                            )
                          }
                          renderValue={(v) => v || 'Select time'}
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            }
                          }}
                        >
                          {CLOSING_TIME_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt}
                              value={opt}
                              disabled={isTimeOptionDisabled(opt)}
                            >
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </DashboardTimeInner>
                  </DialogField>
                  {timeRanges.length > 1 && (
                    <DialogField className='dFlex AlignEnd mTop20'>
                      <DashboardTimeDelete
                        type='button'
                        onClick={() => removeTimeRange(range.id)}
                        aria-label='Remove time range'
                      />
                    </DialogField>
                  )}
                </DialogFields>
              ))}
              {(displayWarning || overlapError) && (
                <ErrorSignWrapper
                  topPosition='-60px'
                  className='marginT10 leftAuto'
                >
                  <ErrorSign>{displayWarning || overlapError}</ErrorSign>
                </ErrorSignWrapper>
              )}
              <DialogField className='w100 dFlex justifyCenter marginT10'>
                <DialogLinkButton type='button' onClick={addTimeRange}>
                  Add time range
                </DialogLinkButton>
              </DialogField>
            </DialogFields>
          )}
          {closedAllDay && (displayWarning || overlapError) && (
            <ErrorSignWrapper
              topPosition='0'
              leftPosition='0'
              className='marginT10'
            >
              <ErrorSign>{displayWarning || overlapError}</ErrorSign>
            </ErrorSignWrapper>
          )}
          <DialogActions className='marginT20'>
            <DialogAction className='w50'>
              <ButtonOutline onClick={handleClose}>Cancel</ButtonOutline>
            </DialogAction>
            <DialogAction className='w50'>
              <ButtonPrimary
                type='button'
                onClick={handleSave}
                disabled={!canSave}
                className={cn('', {disabled: !canSave || loading})}
              >
                <span>{loading ? 'Saving...' : 'Save'}</span>
              </ButtonPrimary>
            </DialogAction>
          </DialogActions>
        </DialogBody>
      </DialogContainer>
    </Dialog>
  );
};

AddSpecialHoursModal.propTypes = {
  open: T.bool.isRequired,
  onClose: T.func.isRequired,
  onSave: T.func.isRequired,
  existingSpecialHours: T.arrayOf(T.object),
  timezone: T.oneOfType([T.string, T.number]),
  loading: T.bool
};

AddSpecialHoursModal.defaultProps = {
  existingSpecialHours: [],
  timezone: null,
  loading: false
};

export default AddSpecialHoursModal;
